<template><div><component-combinator component-name="DtCheckboxGroup" /><p class="d-docsite--paragraph">Checkbox Groups are typically paired with a legend which identifies the group. If no legend is provided then it is expected that an <code v-pre>aria-label</code> will be given in order to provide an invisible label to screen readers. Each Checkbox Group should contain one or more Checkboxes which users can make selections from.</p>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group
  name=&quot;fruits-checkbox-group&quot;
  legend=&quot;Fruits&quot;
  :selectedValues=&quot;[]&quot;
&gt;
  &lt;dt-checkbox value=&quot;apple&quot;&gt;&lt;span&gt;Apple&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;banana&quot;&gt;&lt;span&gt;Banana&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;other&quot;&gt;&lt;span&gt;Other&lt;/span&gt;&lt;/dt-checkbox&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-checkbox-group
  name="fruits-checkbox-group"
  legend="Fruits"
  :selectedValues="[]"
>
  <dt-checkbox value="apple"><span>Apple</span></dt-checkbox>
  <dt-checkbox value="banana"><span>Banana</span></dt-checkbox>
  <dt-checkbox value="other"><span>Other</span></dt-checkbox>
</dt-checkbox-group>
</code-example>
<h3 id="with-selected-values" tabindex="-1" class="d-docsite--header-3">With Selected Values <a class="header-anchor d-link d-docsite--link d-link" href="#with-selected-values" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group
  name=&quot;my-group-name&quot;
  legend=&quot;My Legend&quot;
  :selected-values=&quot;[&#39;option1&#39;]&quot;
&gt;
  &lt;dt-checkbox
    value=&quot;option1&quot;
    label=&quot;Option 1&quot;
  /&gt;
  &lt;dt-checkbox
    value=&quot;option2&quot;
    label=&quot;Option 2&quot;
  /&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-checkbox-group
  name="my-group-name"
  legend="My Legend"
  :selected-values="['option1']"
>
  <dt-checkbox
    value="option1"
    label="Option 1"
  />
  <dt-checkbox
    value="option2"
    label="Option 2"
  />
</dt-checkbox-group>
</code-example>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group
  name=&quot;my-group-name&quot;
  legend=&quot;My Legend&quot;
  disabled
&gt;
  &lt;dt-checkbox
    value=&quot;option1&quot;
    label=&quot;Option 1&quot;
  /&gt;
  &lt;dt-checkbox
    value=&quot;option2&quot;
    label=&quot;Option 2&quot;
  /&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-checkbox-group
  name="my-group-name"
  legend="My Legend"
  disabled
>
  <dt-checkbox
    value="option1"
    label="Option 1"
  />
  <dt-checkbox
    value="option2"
    label="Option 2"
  />
</dt-checkbox-group>
</code-example>
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group
  name=&quot;fruits-checkbox-group&quot;
  legend=&quot;Fruits&quot;
  validation-state=&quot;positive&quot;
  :messages=&quot;[{ message: `Positive validation message.`, type: `positive` }]&quot;
&gt;
  &lt;dt-checkbox value=&quot;apple&quot;&gt;&lt;span&gt;Apple&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;banana&quot;&gt;&lt;span&gt;Banana&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;other&quot;&gt;&lt;span&gt;Other&lt;/span&gt;&lt;/dt-checkbox&gt;
&lt;/dt-checkbox-group&gt;
&lt;dt-checkbox-group
  name=&quot;fruits-checkbox-group&quot;
  legend=&quot;Fruits&quot;
  validation-state=&quot;warning&quot;
  :messages=&quot;[{ message: `Warning validation message.`, type: `warning` }]&quot;
&gt;
  &lt;dt-checkbox value=&quot;apple&quot;&gt;&lt;span&gt;Apple&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;banana&quot;&gt;&lt;span&gt;Banana&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;other&quot;&gt;&lt;span&gt;Other&lt;/span&gt;&lt;/dt-checkbox&gt;
&lt;/dt-checkbox-group&gt;
&lt;dt-checkbox-group
  name=&quot;fruits-checkbox-group&quot;
  legend=&quot;Fruits&quot;
  validation-state=&quot;critical&quot;
  :messages=&quot;[{ message: `Critical validation message.`, type: `critical` }]&quot;
&gt;
  &lt;dt-checkbox value=&quot;apple&quot;&gt;&lt;span&gt;Apple&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;banana&quot;&gt;&lt;span&gt;Banana&lt;/span&gt;&lt;/dt-checkbox&gt;
  &lt;dt-checkbox value=&quot;other&quot;&gt;&lt;span&gt;Other&lt;/span&gt;&lt;/dt-checkbox&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-stack gap="200">
  <div>
    <dt-checkbox-group
      name="checkbox-group-with-positive-message"
      legend="Fruits"
      :messages='[{"message":"Positive validation message","type":"positive"}]'
    >
      <dt-checkbox value="apple"><span>Apple</span></dt-checkbox>
      <dt-checkbox value="banana"><span>Banana</span></dt-checkbox>
      <dt-checkbox value="other"><span>Other</span></dt-checkbox>
    </dt-checkbox-group>
  </div>
  <div>
    <dt-checkbox-group
      name="checkbox-group-with-warning-message"
      legend="Fruits"
      :messages='[{"message":"Warning validation message","type":"warning"}]'
    >
      <dt-checkbox value="apple"><span>Apple</span></dt-checkbox>
      <dt-checkbox value="banana"><span>Banana</span></dt-checkbox>
      <dt-checkbox value="other"><span>Other</span></dt-checkbox>
    </dt-checkbox-group>
  </div>
  <div>
    <dt-checkbox-group
      name="checkbox-group-with-critical-message"
      legend="Fruits"
      :messages='[{"message":"Critical validation message","type":"critical"}]'
    >
      <dt-checkbox value="apple"><span>Apple</span></dt-checkbox>
      <dt-checkbox value="banana"><span>Banana</span></dt-checkbox>
      <dt-checkbox value="other"><span>Other</span></dt-checkbox>
    </dt-checkbox-group>
  </div>
</dt-stack>
</code-example>
<h3 id="with-validation-messages-hidden" tabindex="-1" class="d-docsite--header-3">With Validation Messages Hidden <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-messages-hidden" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group
  name=&quot;my-group-name&quot;
  legend=&quot;My Legend&quot;
  :messages=&quot;[{ message: &#39;My Positive Message&#39;, type: `positive` }]&quot;
  :show-messages=&quot;false&quot;
&gt;
  &lt;dt-checkbox
    value=&quot;option1&quot;
    label=&quot;Option 1&quot;
  /&gt;
  &lt;dt-checkbox
    value=&quot;option2&quot;
    label=&quot;Option 2&quot;
  /&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-checkbox-group
  name="my-group-name"
  legend="My Legend"
  :messages="[{ message: 'My Positive Message', type: `positive` }]"
  :show-messages="false"
>
  <dt-checkbox
    value="option1"
    label="Option 1"
  />
  <dt-checkbox
    value="option2"
    label="Option 2"
  />
</dt-checkbox-group>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="checkboxgroup" /></div></template>


